import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BaseBasketItem,
  IGameResult,
  IPenaltyItem,
  ISettingsItem,
  ISnakesAndLaddersGame,
  ITaskItem,
  PenaltyBasketItem,
  TaskBasketItem,
} from "../ts/Definitions";
import { ControllerContext, SelectedSettingsContext } from "../ts/Contexts";
import Emitter, { ShowMetronomeDialog, ShowSiteEffectsOverlay } from "../utilities/Emitter";
import { defaultSiteEffectsConfig } from "../components/MemberSiteEffectsFramework";
import { toSiteEffectsConfig } from "../components/SiteEffectsSettingsPanel";
import ScheduleItemDialog from "./ScheduleItemDialog";
import { ItemTypes } from "../ts/enums";
import diceGameGif from "../assets/dice-game.gif";

const DICE_FACES = ["", "⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

interface SnakesAndLaddersDialogProps {
  game: ISnakesAndLaddersGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

type ActionType = "task" | "metronome" | "hypnosis";

export interface SquareAction {
  id: string;
  type: ActionType;
  label: string;
  taskKey?: string;
}

export interface Ladder {
  start: number;
  end: number;
}

export interface Snake {
  head: number;
  tail: number;
  action: SquareAction; // always a task action
}

export interface ActionSquare {
  square: number;
  action: SquareAction; // metronome or hypnosis — no backward movement
}

export interface BoardData {
  totalSquares: number;
  ladders: Ladder[];
  snakes: Snake[]; // task-only; landing on head slides player back after task
  actionSquares: ActionSquare[]; // metronome/hypnosis; player stays on square
}

const randInt = (min: number, max: number): number => {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return Math.floor(Math.random() * (hi - lo + 1)) + lo;
};

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const createSnakesAndLaddersBoardData = (
  game: ISnakesAndLaddersGame,
  availableTasks: ITaskItem[]
): BoardData => {
  const squaresPerRow = Math.max(4, game.squaresPerRow || 10);
  const totalSquares = squaresPerRow * squaresPerRow;
  const ladderCount = Math.max(1, Math.floor(Math.sqrt(squaresPerRow)));
  const used = new Set<number>([1, totalSquares]);

  const ladders: Ladder[] = [];
  let ladderAttempts = 0;
  while (ladders.length < ladderCount && ladderAttempts < 500) {
    ladderAttempts++;
    const start = randInt(2, totalSquares - 3);
    if (used.has(start)) continue;
    const maxJump = Math.max(3, Math.floor(totalSquares / 4));
    const end = Math.min(totalSquares - 1, start + randInt(2, maxJump));
    if (end <= start + 1 || used.has(end)) continue;
    ladders.push({ start, end });
    used.add(start);
    used.add(end);
  }

  const repeatCount = Math.max(1, game.taskRepeatCount || 1);
  const selectedTasks = availableTasks.filter((t) => (game.taskKeys || []).includes(t.key));

  // Task actions → snakes (land on head → complete task → slide back to tail)
  const taskActions: SquareAction[] = [];
  selectedTasks.forEach((task) => {
    for (let i = 0; i < repeatCount; i++) {
      taskActions.push({
        id: `${task.key}-${i}`,
        type: "task",
        label: task.name,
        taskKey: task.key,
      });
    }
  });

  // Non-task actions → plain action squares (land on square → trigger action → stay)
  const plainActions: SquareAction[] = [];
  if (game.includeMetronome) {
    for (let i = 0; i < repeatCount; i++) {
      plainActions.push({ id: `metronome-${i}`, type: "metronome", label: "Metronome" });
    }
  }
  if (game.includeHypnosis) {
    for (let i = 0; i < repeatCount; i++) {
      plainActions.push({ id: `hypnosis-${i}`, type: "hypnosis", label: "Hypnosis" });
    }
  }

  // Place task snakes (head + tail pair)
  const snakes: Snake[] = [];
  const shuffledTaskActions = shuffle(taskActions);
  const candidateHeads = shuffle(
    Array.from({ length: totalSquares - 4 }, (_, idx) => idx + 3).filter((n) => !used.has(n))
  );

  // Track placed snake heads to enforce the adjacency constraint.
  const placedSnakeHeads = new Set<number>();

  let taskActionIdx = 0;
  for (const head of candidateHeads) {
    if (taskActionIdx >= shuffledTaskActions.length) break;
    if (used.has(head)) continue;

    // Never place a third snake head in a consecutive run (max 2 adjacent).
    // Also ensure at least one gap of ≥2 safe squares around each cluster so
    // that players always have a chance to avoid snakes on a dice roll (1–6).
    const leftRun =
      (placedSnakeHeads.has(head - 1) ? 1 : 0) +
      (placedSnakeHeads.has(head - 2) ? 1 : 0);
    const rightRun =
      (placedSnakeHeads.has(head + 1) ? 1 : 0) +
      (placedSnakeHeads.has(head + 2) ? 1 : 0);

    // Skip if placing here would create a run of 3 or more.
    const wouldExtendRun =
      (placedSnakeHeads.has(head - 1) && placedSnakeHeads.has(head - 2)) ||
      (placedSnakeHeads.has(head - 1) && placedSnakeHeads.has(head + 1)) ||
      (placedSnakeHeads.has(head + 1) && placedSnakeHeads.has(head + 2));

    // Skip if there is no gap of ≥2 safe squares on either side of a
    // prospective 2-snake cluster (guarantees at least one safe landing in
    // every window of 6 consecutive squares from any approach direction).
    const noSafeGapLeft = leftRun > 0 &&
      !Array.from({ length: 6 }, (_, i) => head - 3 - i)
        .some((sq) => sq >= 1 && !placedSnakeHeads.has(sq) && !used.has(sq));
    const noSafeGapRight = rightRun > 0 &&
      !Array.from({ length: 6 }, (_, i) => head + 3 + i)
        .some((sq) => sq >= 1 && sq <= totalSquares && !placedSnakeHeads.has(sq) && !used.has(sq));

    if (wouldExtendRun || (noSafeGapLeft && noSafeGapRight)) continue;

    const maxSnakeLength = Math.max(2, Math.floor(totalSquares / 6));
    const candidateTails = shuffle(
      Array.from({ length: Math.max(0, head - 2) }, (_, idx) => idx + 1)
        .filter((tail) => !used.has(tail) && tail < head - 1)
    );
    const constrainedTails = candidateTails.filter((tail) => head - tail <= maxSnakeLength);
    const tail = constrainedTails[0] ?? candidateTails[0];
    if (!tail) continue;

    snakes.push({ head, tail, action: shuffledTaskActions[taskActionIdx] });
    placedSnakeHeads.add(head);
    used.add(head);
    used.add(tail);
    taskActionIdx++;
  }

  // Place plain action squares (single square, no tail movement)
  const actionSquares: ActionSquare[] = [];
  const candidatePlain = shuffle(
    Array.from({ length: totalSquares - 2 }, (_, idx) => idx + 2)
      .filter((n) => !used.has(n) && n !== totalSquares)
  );
  shuffle(plainActions).forEach((action, i) => {
    const sq = candidatePlain[i];
    if (!sq) return;
    actionSquares.push({ square: sq, action });
    used.add(sq);
  });

  return {
    totalSquares,
    ladders,
    snakes,
    actionSquares,
  };
};

const SnakesAndLaddersDialog: React.FC<SnakesAndLaddersDialogProps> = ({
  game,
  onClose,
  penaltyKeys,
}) => {
  const settings = useContext(SelectedSettingsContext);
  const controller = useContext(ControllerContext);
  const penalties = useMemo<IPenaltyItem[]>(() =>
    (penaltyKeys || [])
      .map((key) => settings.getPenalty(key))
      .filter((item): item is IPenaltyItem => !!item)
    , [penaltyKeys, settings]);

  const availableTasks = useMemo(
    () => (settings.getTasks() || []).filter((task) => !!task.name),
    [settings]
  );
  const [board] = useState<BoardData>(() => createSnakesAndLaddersBoardData(game, availableTasks));

  const [position, setPosition] = useState(1);
  const [rolls, setRolls] = useState(0);
  const [lastRoll, setLastRoll] = useState<number | null>(null);
  const [statusText, setStatusText] = useState("Roll to start.");
  const [finished, setFinished] = useState(false);
  const [completedActionCount, setCompletedActionCount] = useState(0);
  const [pendingSnake, setPendingSnake] = useState<Snake | null>(null);
  const [showTaskDialog, setShowTaskDialog] = useState<{ taskKey: string; snake: Snake } | null>(null);
  const [moveOverlay, setMoveOverlay] = useState<{ message: string; newPosition: number } | null>(null);
  const [walkAnimation, setWalkAnimation] = useState<{ steps: number[]; stepIdx: number } | null>(null);
  const postWalkActionRef = useRef<(() => void) | null>(null);
  const [isRolling, setIsRolling] = useState(false);

  const [scheduleDialogItem, setScheduleDialogItem] = useState<BaseBasketItem | undefined>(undefined);

  const snakesTaskInScheduleRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!showTaskDialog?.taskKey) {
      setScheduleDialogItem(undefined);
      return;
    }
    const baseItem = (settings.getTask(showTaskDialog.taskKey)
      ?? settings.getPenalty(showTaskDialog.taskKey)) as ISettingsItem | undefined;
    if (!baseItem) { setScheduleDialogItem(undefined); return; }

    const currentItem = (settings.getCurrentItem(baseItem) ?? baseItem) as ISettingsItem;
    const createdItems = settings.createBasketItems(
      currentItem,
      settings.getIsWeekend,
      "Triggered by Snakes and Ladders",
      new Date(),
      undefined,
      undefined,
      false
    );
    if (createdItems.length > 0) {
      setScheduleDialogItem(createdItems[0]);
    } else if (currentItem.itemType === ItemTypes.penalty) {
      setScheduleDialogItem(new PenaltyBasketItem({
        ...(currentItem as unknown as Record<string, unknown>),
        isPaused: true,
        isComplete: false,
        isPenalty: true,
      } as any));
    } else {
      setScheduleDialogItem(new TaskBasketItem({
        ...(currentItem as unknown as Record<string, unknown>),
        isPaused: true,
        isComplete: false,
        isPenalty: false,
      } as any));
    }
  }, [showTaskDialog?.taskKey, settings]);

  // Auto-apply the position change after 5 seconds
  useEffect(() => {
    if (!moveOverlay) return;
    const timer = setTimeout(() => {
      setPosition(moveOverlay.newPosition);
      setMoveOverlay(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [moveOverlay]);

  // Step-by-step walk animation: advance one square every 300ms
  useEffect(() => {
    if (!walkAnimation) return;
    if (walkAnimation.stepIdx >= walkAnimation.steps.length) {
      setWalkAnimation(null);
      postWalkActionRef.current?.();
      postWalkActionRef.current = null;
      return;
    }
    const timer = setTimeout(() => {
      setPosition(walkAnimation.steps[walkAnimation.stepIdx]);
      setWalkAnimation(prev => prev ? { ...prev, stepIdx: prev.stepIdx + 1 } : null);
    }, 300);
    return () => clearTimeout(timer);
  }, [walkAnimation]);

  const ladderByStart = useMemo(() => {
    const map = new Map<number, Ladder>();
    board.ladders.forEach((ladder) => map.set(ladder.start, ladder));
    return map;
  }, [board.ladders]);

  const snakeByHead = useMemo(() => {
    const map = new Map<number, Snake>();
    board.snakes.forEach((snake) => map.set(snake.head, snake));
    return map;
  }, [board.snakes]);

  const actionSquareBySquare = useMemo(() => {
    const map = new Map<number, ActionSquare>();
    board.actionSquares.forEach((as) => map.set(as.square, as));
    return map;
  }, [board.actionSquares]);

  const legendCounts = useMemo(() => ({
    ladders: board.ladders.length,
    taskSnakes: board.snakes.length,
    metronome: board.actionSquares.filter((s) => s.action.type === "metronome").length,
    hypnosis: board.actionSquares.filter((s) => s.action.type === "hypnosis").length,
  }), [board]);

  const squaresPerRow = Math.max(4, game.squaresPerRow || 10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const applySnakeAfterAction = (snake: Snake) => {
    setCompletedActionCount((v) => v + 1);
    setPendingSnake(null);
    setShowTaskDialog(null);
    setMoveOverlay({
      message: `Task complete! Snake slides you back from square ${snake.head} to square ${snake.tail}.`,
      newPosition: snake.tail,
    });
  };

  const openPlainAction = (action: SquareAction) => {
    if (action.type === "metronome") {
      Emitter.emit(ShowMetronomeDialog, {
        title: "Metronome Challenge",
        taskKey: game.metronomeTaskKey,
        audioUrl: game.metronomeAudioUrl,
      });
    } else if (action.type === "hypnosis") {
      const config = toSiteEffectsConfig(game.hypnosisSettings ?? defaultSiteEffectsConfig);
      const durationSec = game.hypnosisDurationSec ?? randInt(20, 45);
      Emitter.emit(ShowSiteEffectsOverlay, {
        title: "Hypnosis Challenge",
        config,
        durationSec,
        ownerTest: false,
      });
    }
  };

  const handleRoll = () => {
    if (finished || pendingSnake || moveOverlay || showTaskDialog || walkAnimation || isRolling) return;
    const roll = randInt(1, 6);
    setLastRoll(roll);
    setRolls((v) => v + 1);

    const target = Math.min(board.totalSquares, position + roll);

    // Build intermediate squares to walk through one at a time
    const steps: number[] = [];
    for (let sq = position + 1; sq <= target; sq++) {
      steps.push(sq);
    }

    // Capture what should happen once the piece finishes walking
    if (target >= board.totalSquares) {
      postWalkActionRef.current = () => {
        setFinished(true);
        setStatusText("You reached the final square and completed the game.");
      };
    } else {
      const ladder = ladderByStart.get(target);
      if (ladder) {
        postWalkActionRef.current = () => {
          setMoveOverlay({
            message: `Rolled ${roll}. Ladder! Climbing from square ${ladder.start} up to square ${ladder.end}.`,
            newPosition: ladder.end,
          });
          setStatusText(`Rolled ${roll}. Ladder!`);
        };
      } else {
        const snake = snakeByHead.get(target);
        if (snake) {
          postWalkActionRef.current = () => {
            setPendingSnake(snake);
            setShowTaskDialog({ taskKey: snake.action.taskKey || "", snake });
            setStatusText(`Rolled ${roll}. Task at square ${target}: ${snake.action.label}. Complete the task to continue.`);
          };
        } else {
          const actionSquare = actionSquareBySquare.get(target);
          if (actionSquare) {
            postWalkActionRef.current = () => {
              setStatusText(`Rolled ${roll}. ${actionSquare.action.label} at square ${target}.`);
              openPlainAction(actionSquare.action);
            };
          } else {
            postWalkActionRef.current = () => {
              setStatusText(`Rolled ${roll}. Moved to square ${target}.`);
            };
          }
        }
      }
    }

    setIsRolling(true);
    setTimeout(() => {
      setIsRolling(false);
      setStatusText(`Rolled ${roll}. Moving...`);
      setWalkAnimation({ steps, stepIdx: 0 });
    }, 900);
  };

  const closeGame = () => {
    onClose({
      gameKey: game.key,
      penaltyCount: 0,
      completed: completedActionCount,
      total: board.snakes.length,
      totalTime: rolls,
      mistakes: 0,
      userid: game.userid,
      username: game.username,
      date: new Date(),
    });
  };

  const getSquareNumber = (row: number, col: number): number => {
    const fromBottom = squaresPerRow - 1 - row;
    const rowStart = fromBottom * squaresPerRow + 1;
    const isLeftToRight = fromBottom % 2 === 0;
    return isLeftToRight ? rowStart + col : rowStart + (squaresPerRow - 1 - col);
  };

  return (
    <>
      <style>{`
        @keyframes playerPulse {
          0%   { box-shadow: 0 0 0  0px rgba(33,150,243,0.7); }
          70%  { box-shadow: 0 0 0 14px rgba(33,150,243,0);   }
          100% { box-shadow: 0 0 0  0px rgba(33,150,243,0);   }
        }
      `}</style>

      <Dialog open maxWidth={isMobile ? false : "lg"} fullWidth fullScreen={isMobile} onClose={() => { }}>
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1b5e20 0%, #4a148c 100%)",
            color: "white",
            fontSize: isMobile ? "1rem" : "1.3rem",
            fontWeight: 700,
            py: isMobile ? 1 : 1.5,
            letterSpacing: 0.5,
          }}
        >
          🐍 Snakes &amp; Ladders 🪜
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden", height: isMobile ? "100%" : "78vh" }}>
          {/* ── Mobile compact stats strip ── */}
          {isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, background: "#f1f8e9", borderBottom: "2px solid #a5d6a7", flexShrink: 0 }}>
              <Box sx={{ textAlign: "center", minWidth: 36 }}>
                {isRolling ? (
                  <Box component="img" src={diceGameGif} alt="Rolling" sx={{ width: 32, height: 32, borderRadius: 1 }} />
                ) : (
                  <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{lastRoll ? DICE_FACES[lastRoll] : "🎲"}</Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {[
                  { label: "Sq", value: `${moveOverlay ? moveOverlay.newPosition : position}/${board.totalSquares}`, color: "#1565c0" },
                  { label: "Rolls", value: String(rolls), color: "#333" },
                  { label: "Tasks", value: `${completedActionCount}/${board.snakes.length}`, color: "#c62828" },
                ].map(({ label, value, color }) => (
                  <Box key={label} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 9, color: "#777", lineHeight: 1 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Board area ── */}
          <Box sx={{ flex: 1, overflow: "auto", p: isMobile ? 1 : 2 }}>
            {moveOverlay && (
              <Alert severity="info" sx={{ mb: 1, fontWeight: 600, fontSize: isMobile ? 11 : undefined }}>
                {moveOverlay.message}
              </Alert>
            )}
            {penalties.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Penalty: <b>{penalties.map((p) => p.name).join(", ")}</b>
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${squaresPerRow}, minmax(${isMobile ? 0 : 54}px, 1fr))`,
                gap: isMobile ? "2px" : "4px",
                border: `${isMobile ? 3 : 5}px solid #1b5e20`,
                borderRadius: 2,
                p: isMobile ? "3px" : "6px",
                background: "#2e7d32",
              }}
            >
              {Array.from({ length: squaresPerRow * squaresPerRow }).map((_, idx) => {
                const row = Math.floor(idx / squaresPerRow);
                const col = idx % squaresPerRow;
                const number = getSquareNumber(row, col);

                const isLadderStart = ladderByStart.has(number);
                const isLadderEnd = board.ladders.some((l) => l.end === number);
                const snake = snakeByHead.get(number);
                const isSnakeHead = !!snake;
                const isSnakeTail = board.snakes.some((s) => s.tail === number);
                const actionSq = actionSquareBySquare.get(number);
                const isPlayer = !moveOverlay && position === number;
                const isMovingFrom = !!moveOverlay && position === number;
                const isMovingTo = !!moveOverlay && moveOverlay.newPosition === number;
                const isFirstSquare = number === 1;
                const isLastSquare = number === board.totalSquares;

                const fromBottom = squaresPerRow - 1 - row;
                const isEven = (fromBottom + col) % 2 === 0;
                let bg = isEven ? "#fffde7" : "#fff8e1";
                let borderColor = isEven ? "#e8d5a3" : "#d4c080";
                let emoji = "";
                let subLabel = "";
                let textColor = "#333";

                if (isLastSquare) {
                  bg = "#ffd54f"; borderColor = "#f9a825"; emoji = "🏆";
                } else if (isFirstSquare) {
                  bg = "#c8e6c9"; borderColor = "#66bb6a"; emoji = "🏁";
                } else if (isSnakeHead) {
                  bg = "#c62828"; borderColor = "#7f0000"; emoji = "🐍";
                  subLabel = snake!.action.label.length > 10 ? snake!.action.label.slice(0, 10) + "…" : snake!.action.label;
                  textColor = "#fff";
                } else if (isSnakeTail) {
                  bg = "#ef9a9a"; borderColor = "#e53935"; emoji = "🔻"; textColor = "#7f0000";
                } else if (isLadderStart) {
                  bg = "#2e7d32"; borderColor = "#1b5e20"; emoji = "🪜"; subLabel = "Ladder"; textColor = "#fff";
                } else if (isLadderEnd) {
                  bg = "#a5d6a7"; borderColor = "#4caf50"; emoji = "⬆️"; textColor = "#1b5e20";
                } else if (actionSq?.action.type === "metronome") {
                  bg = "#1565c0"; borderColor = "#003c8f"; emoji = "🎵"; subLabel = "Metro"; textColor = "#fff";
                } else if (actionSq?.action.type === "hypnosis") {
                  bg = "#6a1b9a"; borderColor = "#38006b"; emoji = "👁️"; subLabel = "Hypno"; textColor = "#fff";
                }

                return (
                  <Paper
                    key={number}
                    elevation={isPlayer ? 6 : 1}
                    sx={{
                      p: isMobile ? "1px" : "2px",
                      minHeight: isMobile ? 34 : 62,
                      borderRadius: isMobile ? "3px" : "6px",
                      border: isPlayer
                        ? `${isMobile ? 2 : 3}px solid #42a5f5`
                        : isMovingFrom
                          ? `${isMobile ? 1 : 2}px dashed #ef6c00`
                          : isMovingTo
                            ? `${isMobile ? 2 : 3}px solid #66bb6a`
                            : `1px solid ${borderColor}`,
                      backgroundColor: bg,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      position: "relative",
                      overflow: "hidden",
                      transition: "box-shadow 0.15s",
                      animation: isPlayer ? "playerPulse 1s infinite" : undefined,
                    }}
                  >
                    {!isMobile && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 800, fontSize: 9, color: textColor, opacity: 0.75, alignSelf: "flex-start", lineHeight: 1.1, px: "2px" }}
                      >
                        {number}
                      </Typography>
                    )}

                    {emoji && !isPlayer && (
                      <Typography sx={{ fontSize: isMobile ? (isSnakeHead || isLadderStart ? 14 : 11) : (isSnakeHead || isLadderStart ? 24 : 18), lineHeight: 1, my: "1px" }}>
                        {emoji}
                      </Typography>
                    )}

                    {isPlayer && (
                      <Typography
                        sx={{
                          fontSize: isMobile ? 16 : 26,
                          lineHeight: 1,
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 2,
                          filter: "drop-shadow(0 0 4px rgba(33,150,243,0.9))",
                        }}
                      >
                        🔵
                      </Typography>
                    )}

                    {isMovingTo && (
                      <Typography
                        sx={{
                          fontSize: isMobile ? 12 : 20,
                          lineHeight: 1,
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 2,
                          opacity: 0.7,
                        }}
                      >
                        🎯
                      </Typography>
                    )}

                    {subLabel && !isMobile && (
                      <Typography variant="caption" sx={{ fontSize: 8, color: textColor, textAlign: "center", lineHeight: 1.1, px: "2px", pb: "1px" }}>
                        {subLabel}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>

          {/* ── Side control panel (desktop only) ── */}
          {!isMobile && <Box
            sx={{
              width: 190,
              minWidth: 190,
              borderLeft: "3px solid #2e7d32",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              p: 1.5,
              background: "linear-gradient(180deg, #f9fbe7 0%, #f1f8e9 100%)",
              overflow: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1b5e20", textAlign: "center", letterSpacing: 0.5 }}>
              🎮 Game Stats
            </Typography>

            {/* Dice display */}
            <Box sx={{ textAlign: "center", background: "#fff", borderRadius: 2, p: 1, border: "2px solid #a5d6a7" }}>
              {isRolling ? (
                <Box
                  component="img"
                  src={diceGameGif}
                  alt="Rolling dice..."
                  sx={{ width: 68, height: 68, borderRadius: 1 }}
                />
              ) : (
                <Typography sx={{ fontSize: 56, lineHeight: 1 }}>
                  {lastRoll ? DICE_FACES[lastRoll] : "🎲"}
                </Typography>
              )}
              {lastRoll && !isRolling && (
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "#1b5e20" }}>
                  Rolled {lastRoll}
                </Typography>
              )}
              {!lastRoll && (
                <Typography variant="caption" sx={{ display: "block", color: "#888" }}>
                  Roll to start
                </Typography>
              )}
            </Box>

            {/* Stat boxes */}
            {[
              { label: "Position", value: `${moveOverlay ? moveOverlay.newPosition : position} / ${board.totalSquares}`, color: "#1565c0" },
              { label: "Rolls", value: String(rolls), color: "#333" },
              { label: "Tasks Done", value: `${completedActionCount} / ${board.snakes.length}`, color: "#c62828" },
            ].map(({ label, value, color }) => (
              <Paper key={label} elevation={1} sx={{ p: 0.75, textAlign: "center", background: "#fff", borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ color: "#777", display: "block", fontSize: 10 }}>{label}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color }}>{value}</Typography>
              </Paper>
            ))}

            {/* Status text */}
            <Box sx={{ background: "#fff8e1", borderRadius: 1.5, p: 1, border: "1px solid #ffe082" }}>
              <Typography variant="caption" sx={{ color: "#5d4037", fontWeight: 500, fontSize: 10 }}>
                {statusText}
              </Typography>
            </Box>

            {/* Legend */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.5 }}>
                Legend
              </Typography>
              {[
                { emoji: "🔵", label: "You" },
                { emoji: "🐍", label: `Snake (${legendCounts.taskSnakes})` },
                { emoji: "🪜", label: `Ladder (${legendCounts.ladders})` },
                ...(legendCounts.metronome > 0 ? [{ emoji: "🎵", label: `Metronome (${legendCounts.metronome})` }] : []),
                ...(legendCounts.hypnosis > 0 ? [{ emoji: "👁️", label: `Hypnosis (${legendCounts.hypnosis})` }] : []),
                { emoji: "🏆", label: "Finish" },
              ].map(({ emoji, label }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.2 }}>{emoji}</Typography>
                  <Typography variant="caption" sx={{ fontSize: 10 }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Roll button */}
            <Button
              variant="contained"
              onClick={handleRoll}
              disabled={finished || !!pendingSnake || !!moveOverlay || !!walkAnimation || isRolling}
              sx={{
                background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
                color: "white",
                fontWeight: 700,
                fontSize: "0.95rem",
                py: 1.25,
                borderRadius: 2,
                boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
                "&:hover": {
                  background: "linear-gradient(135deg, #388e3c 0%, #4caf50 100%)",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                },
                "&:disabled": { background: "#bdbdbd", color: "#fff" },
              }}
            >
              {isRolling ? "🎲 Rolling…" : finished ? "✓ Done" : "🎲 Roll Dice"}
            </Button>

            {finished && (
              <Button
                variant="contained"
                onClick={closeGame}
                sx={{
                  background: "linear-gradient(135deg, #f57f17 0%, #ff8f00 100%)",
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                🏆 Close
              </Button>
            )}
          </Box>}

          {/* ── Mobile bottom bar ── */}
          {isMobile && (
            <Box sx={{ flexShrink: 0, borderTop: "2px solid #a5d6a7", background: "#f1f8e9", p: 1 }}>
              {statusText && (
                <Typography sx={{ fontSize: 10, color: "#5d4037", mb: 0.75, textAlign: "center" }}>
                  {statusText}
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRoll}
                  disabled={finished || !!pendingSnake || !!moveOverlay || !!walkAnimation || isRolling}
                  sx={{
                    background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    py: 1,
                    borderRadius: 2,
                    "&:disabled": { background: "#bdbdbd", color: "#fff" },
                  }}
                >
                  {isRolling ? "🎲 Rolling…" : finished ? "✓ Done" : "🎲 Roll"}
                </Button>
                {finished && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={closeGame}
                    sx={{ background: "linear-gradient(135deg, #f57f17 0%, #ff8f00 100%)", color: "white", fontWeight: 700, borderRadius: 2, py: 1 }}
                  >
                    🏆 Close
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {showTaskDialog && scheduleDialogItem && (
        <ScheduleItemDialog
          item={scheduleDialogItem}
          play={(item) => {
            const basketItem = item as unknown as TaskBasketItem;
            // If option already selected, nothing more to do (Done will call callback)
            if ((basketItem as any).selectedCondition) return;
            const itemKey = basketItem.key;
            // Use owner (logged-on-user) settings — same instance as SchedulerManager.userSettings —
            // so the taskSchedule round-trip works regardless of which settings the context provides.
            const ownerSettings = controller.getLoggedOnUserSettings();
            // Get options from the task definition
            const task = (
              settings.getTask(itemKey) ??
              settings.getPenalty(itemKey)
            ) as ITaskItem | IPenaltyItem | undefined;
            const options = (task as any)?.options as string[] | undefined;
            if (TaskBasketItem.NeedsRouletteToChooseOption(basketItem as any, task as any)) {
              // Show roulette to pick an option, then update the dialog item when a result comes back
              ownerSettings.updateTaskState(basketItem as unknown as Parameters<typeof ownerSettings.updateTaskState>[0]);
              snakesTaskInScheduleRef.current = itemKey;
              controller.handleRouletteTask(
                basketItem as unknown as ITaskItem | IPenaltyItem,
                options
              );
              // Listen on ownerSettings — same instance that setRouletteResult writes to.
              const onRouletteResult = () => {
                const entry = ownerSettings.getTaskSchedule()[itemKey];
                if (!entry || !(entry as any).selectedCondition) return;
                const updated = (entry as any).isPenalty
                  ? new PenaltyBasketItem(entry as any)
                  : new TaskBasketItem(entry as any);
                setScheduleDialogItem(updated);
                ownerSettings.unsubscribeToViewChanges(onRouletteResult);
              };
              ownerSettings.subscribeToViewChanges(onRouletteResult);
            } else {
              // Timed tasks, lines, etc. — delegate to the normal play path which
              // starts the timer/countdown via the scheduler.
              controller.handlePlayItem(item as unknown as ITaskItem | IPenaltyItem);
            }
          }}
          showRemainingTime={false}
          defaultImage={""}
          isOwned={true}
          readonly={false}
          checkEventComplete={() => false}
          onSpin={() => { /* roulette is triggered via the play prop above */ }}
          callback={() => {
            if (!showTaskDialog) return;
            // Clean up any temporary taskSchedule entry we added for roulette
            if (snakesTaskInScheduleRef.current) {
              controller.getLoggedOnUserSettings().deleteTaskState(snakesTaskInScheduleRef.current);
              snakesTaskInScheduleRef.current = undefined;
            }
            applySnakeAfterAction(showTaskDialog.snake);
          }}
        />
      )}
    </>
  );
};

export default SnakesAndLaddersDialog;
